import { io, Socket } from 'socket.io-client';
import { IConversationEnriched, IMessage } from '@eagerdog/interfaces';
import { Config } from '@eagerdog/config';
import { httpService } from './http.service';
import { localStorageService } from './local-storage.service';

const BASE = `${Config.web.REACT_APP_API_URL}/api/conversations`;

// --------------------------------------------------
// REST API
// --------------------------------------------------

const getConversations = async (): Promise<IConversationEnriched[]> => {
  const res = await httpService.get<IConversationEnriched[]>(BASE);
  return res.data;
};

const getUnreadCount = async (): Promise<{ count: number }> => {
  const res = await httpService.get<{ count: number }>(`${BASE}/unread-count`);
  return res.data;
};

export interface IRecipientInfo {
  _id: string;
  display_name: string;
  email: string;
}

/** Resolve a dog_id or recipient_id to the recipient's display info (name + email). */
const getRecipientInfo = async (
  dogId?: string,
  recipientId?: string,
): Promise<IRecipientInfo> => {
  const params: any = {};
  if (dogId) params.dog_id = dogId;
  if (recipientId) params.recipient_id = recipientId;
  const res = await httpService.get<IRecipientInfo>(`${BASE}/recipient-info`, { params });
  return res.data;
};

/** Convenience wrapper: resolve a user _id directly to display info. */
const getRecipientInfoByUserId = (userId: string): Promise<IRecipientInfo> =>
  getRecipientInfo(undefined, userId);

/** Find an existing conversation without creating one. Returns null if not found. */
const findConversation = async (
  recipientId?: string,
  dogId?: string,
): Promise<IConversationEnriched | null> => {
  const params: any = {};
  if (recipientId) params.recipient_id = recipientId;
  if (dogId) params.dog_id = dogId;
  const res = await httpService.get<IConversationEnriched | null>(`${BASE}/find`, { params });
  return res.data;
};

const getMessages = async (
  conversationId: string,
  beforeId?: string,
  limit = 50,
): Promise<IMessage[]> => {
  const params: any = { limit };
  if (beforeId) params.before = beforeId;
  const res = await httpService.get<IMessage[]>(`${BASE}/${conversationId}/messages`, { params });
  return res.data;
};

const sendMessage = async (
  conversationId: string,
  content: string,
): Promise<IMessage> => {
  const res = await httpService.post<IMessage>(`${BASE}/${conversationId}/messages`, { content });
  return res.data;
};

/**
 * Send the first message — atomically creates the conversation on the backend.
 * Pass either recipientId (user id) or dogId (dog id) to identify the recipient.
 */
const initiateConversation = async (
  content: string,
  recipientId?: string,
  dogId?: string,
): Promise<{ conversation: IConversationEnriched; message: IMessage }> => {
  const body: any = { content };
  if (recipientId) body.recipient_id = recipientId;
  if (dogId) body.dog_id = dogId;
  const res = await httpService.post<{ conversation: IConversationEnriched; message: IMessage }>(
    `${BASE}/initiate`,
    body,
  );
  return res.data;
};

const markAsRead = async (conversationId: string): Promise<void> => {
  await httpService.put(`${BASE}/${conversationId}/read`, {});
};

// --------------------------------------------------
// Socket.io — /messages namespace
// --------------------------------------------------

let socket: Socket | null = null;

const connectSocket = (): void => {
  if (socket?.connected) return;

  const token = localStorageService.getItem(localStorageService.ACCESS_TOKEN);
  if (!token) return;

  socket = io(`${Config.web.REACT_APP_API_URL}/messages`, {
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 2000,
    reconnectionDelayMax: 30000,
  });
};

const disconnectSocket = (): void => {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
};

const onNewMessage = (
  cb: (message: IMessage, conversation: IConversationEnriched) => void,
): (() => void) => {
  if (!socket) return () => {};
  const handler = (data: { message: IMessage; conversation: IConversationEnriched }) => {
    cb(data.message, data.conversation);
  };
  socket.on('message.new', handler);
  return () => socket?.off('message.new', handler);
};

const onUnreadCount = (cb: (count: number) => void): (() => void) => {
  if (!socket) return () => {};
  const handler = (data: { count: number }) => cb(data.count);
  socket.on('unread.count', handler);
  return () => socket?.off('unread.count', handler);
};

const onConversationRead = (cb: (conversationId: string) => void): (() => void) => {
  if (!socket) return () => {};
  const handler = (data: { conversation_id: string }) => cb(data.conversation_id);
  socket.on('conversation.read', handler);
  return () => socket?.off('conversation.read', handler);
};

/** Fires whenever the socket (re)connects — use to re-sync server-side state. */
const onConnect = (cb: () => void): (() => void) => {
  if (!socket) return () => {};
  socket.on('connect', cb);
  return () => socket?.off('connect', cb);
};

export const messageService = {
  getConversations,
  getUnreadCount,
  getRecipientInfo,
  getRecipientInfoByUserId,
  findConversation,
  getMessages,
  sendMessage,
  initiateConversation,
  markAsRead,
  connectSocket,
  disconnectSocket,
  onNewMessage,
  onUnreadCount,
  onConversationRead,
  onConnect,
};
